import type { HighlightedLineProps } from "@telia/teddy/dist/components/shopping-cart/shopping-cart-payment";
import _cloneDeep from "lodash/cloneDeep";
import _compact from "lodash/compact";
import _endsWith from "lodash/endsWith";
import _find from "lodash/find";
import _flattenDeep from "lodash/flattenDeep";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import _size from "lodash/size";
import _startsWith from "lodash/startsWith";
import { getCheckout, State as CheckoutState } from "./checkout";
import {
    getLegalSubscription,
    getSubscriptions,
    isSubscriptionBroadband,
    isServiceSubscription,
    isTeliaTryggSubscription,
} from "./subscriptionsSelectors";
import {
    Realm,
    CART_ITEM_SUBSCRIPTION_TYPES,
    CART_ITEM_TYPE,
    CART_PAYMENT_TYPE,
    CartItemTypeValues,
    CartPaymentTypeValue,
    REALM,
    CustomerType,
    CUSTOMER_TYPE,
    TELIA_TRYGG_SERVICE,
} from "../../../../../constants";
import {
    EsimType,
    GetCustomerType,
    ICartItem,
    ICartItemWithBundleId,
    ICartSubtitleItem,
    OfferingCode,
} from "../../../../../models";
import { CartState } from "../../../../../models/state";
import { mapIconName } from "../../../../../shared/mapIconName";
import { mapCartDiscountTypes } from "../../../../../shared/mapLozengeStatusToBadgeVariant";
import { getFormattedPrice } from "../../../../../shared/priceFormatter";

export type State = CheckoutState;

export const getCart: (state: State) => CartState = (state) => getCheckout(state).cart || {};
export const getActiveSubscription: (state: State) => string | undefined = (state) => getCart(state).activeSubscription;
const setSubtitleForCartItem = (cartItem: ICartItem, isActive: boolean, isDraft: boolean) => {
    if (!CART_ITEM_SUBSCRIPTION_TYPES.includes(cartItem.type)) {
        return cartItem;
    }

    const subtitle = _cloneDeep(cartItem.subtitle) || [];
    if (isActive) {
        subtitle?.push("Tildeles nå");
    } else if (isDraft) {
        subtitle?.push("Fortsett bestillingen for å tildele");
    }
    return { ...cartItem, subtitle };
};
const setSubtitleForSubItems = (cartItem: ICartItem, isActive: boolean) => {
    let subItems = _cloneDeep(cartItem.items) || [];
    if (cartItem.items) {
        subItems = subItems.map((subItem) => {
            const isSubItemDraft = subItem.type === CART_ITEM_TYPE.SUBSCRIPTION_DRAFT;
            return setSubtitleForCartItem(subItem, isActive, isSubItemDraft);
        });
    }
    return subItems;
};
export const getCartContent: (state: State) => ICartItem[] = (state) => {
    const activeSubscription = getActiveSubscription(state);
    const hasMultipleSubscriptions = _size(getSubscriptions(state)) > 1;

    return (getCart(state).content || []).map((item) => {
        if (!hasMultipleSubscriptions) {
            return item;
        }

        const isDraft = item.type === CART_ITEM_TYPE.SUBSCRIPTION_DRAFT;
        const isActive = item.bundleId === activeSubscription;

        item = setSubtitleForCartItem(item, isActive, isDraft);
        const subItems = setSubtitleForSubItems(item, isActive);

        return {
            ...item,
            items: subItems,
            status: {
                isDraft,
                isActive,
            },
        };
    });
};

const mapDiscountTypesForItem = (item: ICartItem): ICartItem => ({
    ...item,
    image:
        item.image && "icon" in item.image
            ? { ...item.image, icon: mapIconName(item.image.icon) as typeof item.image.icon }
            : item.image,
    discount: item.discount ? { ...item.discount, types: mapCartDiscountTypes(item.discount.types) } : item.discount,
    items: item.items?.map(mapDiscountTypesForItem),
});

export const getCartContentPreparedForRealm: (state: State, realm?: Realm) => ICartItem[] = (
    state,
    realm = REALM.B2C,
) => {
    const content = getCartContent(state).map(mapDiscountTypesForItem);

    if (realm === REALM.B2B) {
        return content.map((item) => ({
            ...item,
            price: {
                ...item.price,
                upfront: _get(item, "price.upfrontWithoutVAT", item.price.upfront),
                monthly: _get(item, "price.monthlyWithoutVAT", item.price.monthly),
            },
        }));
    }

    return content;
};

export const getBookStartTime: (state: State) => string | undefined = (state) => getCart(state).bookStartTime;
export const getBookEndTime: (state: State) => string | undefined = (state) => getCart(state).bookEndTime;
export const getCustomerTypeGet: (state: State) => GetCustomerType | null = (state) =>
    getCart(state).customerTypeGet || null;
export const hasPaid: (state: State) => boolean = (state) => getCart(state).hasPaid || false;
export const getPortingDate: (state: State) => string | undefined = (state) => getCart(state).portingDate;
export const getPortingDates: (state: State) => Record<string, string> = (state) => getCart(state).portingDates ?? {};
export const cartItemReducer = (flattenedItems: ICartItem[], item?: ICartItem): ICartItem[] => {
    if (!item) {
        return flattenedItems;
    } else if (_isEmpty(item.items)) {
        return [...flattenedItems, item];
    } else {
        return (item.items || []).reduce(cartItemReducer, [...flattenedItems, item]);
    }
};
export const getFlattenedCartContent = (state: State): ICartItem[] => {
    return getCartContent(state).reduce(cartItemReducer, []);
};

export const getNumberOfItemsInCart: (state: State) => number = (state) =>
    getFlattenedCartContent(state).filter((item) => {
        switch (item.type) {
            case CART_ITEM_TYPE.HANDSET:
            case CART_ITEM_TYPE.HANDSET_PREOWNED:
            case CART_ITEM_TYPE.ACCESSORY:
            case CART_ITEM_TYPE.WEARABLE:
            case CART_ITEM_TYPE.TABLET:
            case CART_ITEM_TYPE.MODEM:
                // If bundleId != null => we have a bundle with sub + hwItem => only count 1 of them
                return !item.bundleId || _size(item.items) === 0;
            case CART_ITEM_TYPE.CONNECTED_DEVICE:
            case CART_ITEM_TYPE.SIM:
            case CART_ITEM_TYPE.SUBSCRIPTION:
            case CART_ITEM_TYPE.SUBSCRIPTION_DRAFT:
                return true;
            default:
                return false;
        }
    }).length;
export const getAllItemsOfType = (state: State, ...type: CartItemTypeValues[]): ICartItem[] => {
    const types = _flattenDeep(type);
    return getFlattenedCartContent(state).filter((item) => types.includes(item.type));
};

export const lastAddedCartItem: (state: State) => ICartItem = (state) => {
    const cartContent = getCartContent(state);
    return cartContent[cartContent.length - 1];
};

export const getAllCartSkus: (state: State) => string[] = (state) => {
    const types: CartItemTypeValues[] = [
        CART_ITEM_TYPE.HANDSET,
        CART_ITEM_TYPE.ACCESSORY,
        CART_ITEM_TYPE.WEARABLE,
        CART_ITEM_TYPE.CONNECTED_DEVICE,
        CART_ITEM_TYPE.TABLET,
        CART_ITEM_TYPE.MODEM,
        CART_ITEM_TYPE.SIM,
    ];
    return getFlattenedCartContent(state)
        .filter((item) => types.includes(item.type))
        .map((item) => item.id);
};
export const isSkuInCart: (state: State, sku: string) => boolean = (state, sku) =>
    !!getAllCartSkus(state).find((item) => item === sku);
export const getCartItemBySku: (state: State, sku: string) => ICartItem | undefined = (state, sku) =>
    getFlattenedCartContent(state).find((item) => item.id === sku);
export const getCartItemByCartItemId: (state: State, cartItemId: string) => ICartItem | undefined = (
    state,
    cartItemId,
) => getFlattenedCartContent(state).find((item) => item.cartItemId === cartItemId);
export const getItemByBundleId: (state: State, id?: string) => ICartItemWithBundleId[] = (state, id) =>
    getCartContent(state).filter((item): item is ICartItemWithBundleId => !!id && item.bundleId === id);
export const isCartExpanded: (state: State) => boolean = (state) => getCart(state).isExpanded || false;

// Hardware
export const hasHandsetItems: (state: State) => boolean = (state) =>
    getAllItemsOfType(state, CART_ITEM_TYPE.HANDSET).length > 0;
export const hasPreownedHandsetItems: (state: State) => boolean = (state) =>
    getAllItemsOfType(state, CART_ITEM_TYPE.HANDSET_PREOWNED).length > 0;
export const hasAccessoryItems: (state: State) => boolean = (state) =>
    getAllItemsOfType(state, CART_ITEM_TYPE.ACCESSORY).length > 0;
export const hasWebdealsItems: (state: State) => boolean = (state) =>
    getAllItemsOfType(state, CART_ITEM_TYPE.WEBDEAL).length > 0;
export const hasModemItems: (state: State) => boolean = (state) =>
    getAllItemsOfType(state, CART_ITEM_TYPE.MODEM).length > 0;
export const hasTabletItems: (state: State) => boolean = (state) =>
    getAllItemsOfType(state, CART_ITEM_TYPE.TABLET).length > 0;
export const hasSpecialProductItems: (state: State) => boolean = (state) =>
    getAllItemsOfType(state, CART_ITEM_TYPE.SPECIALPRODUCT).length > 0;
export const hasWearableItems: (state: State) => boolean = (state) =>
    getAllItemsOfType(state, CART_ITEM_TYPE.WEARABLE).length > 0;
export const hasConnectedDeviceItems: (state: State) => boolean = (state) =>
    getAllItemsOfType(state, CART_ITEM_TYPE.CONNECTED_DEVICE).length > 0;
export const hasMobileRefundItems: (state: State) => boolean = (state) =>
    getAllItemsOfType(state, CART_ITEM_TYPE.MOBILE_REFUND).length > 0;
export const hasCartHardware: (state: State) => boolean = (state) =>
    hasHandsetItems(state) ||
    hasPreownedHandsetItems(state) ||
    hasAccessoryItems(state) ||
    hasWebdealsItems(state) ||
    hasModemItems(state) ||
    hasTabletItems(state) ||
    hasConnectedDeviceItems(state) ||
    hasWearableItems(state);
export const hasReSwitchHandset: (state: State) => boolean = (state) =>
    !!getAllItemsOfType(state, CART_ITEM_TYPE.HANDSET).find((handset) => handset.isReSwitch);
export const hasItemsWithIsWebdeal: (state: State) => boolean = (state) =>
    getFlattenedCartContent(state).some((item) => item.isWebDeal);

export const getAllHardwareBundleItems: (state: State) => ICartItemWithBundleId[] = (state) => {
    const hardwareTypes: CartItemTypeValues[] = [
        CART_ITEM_TYPE.HANDSET,
        CART_ITEM_TYPE.HANDSET_PREOWNED,
        CART_ITEM_TYPE.ACCESSORY,
        CART_ITEM_TYPE.WEARABLE,
        CART_ITEM_TYPE.TABLET,
        CART_ITEM_TYPE.MODEM,
        CART_ITEM_TYPE.CONNECTED_DEVICE,
    ];
    return getCartContent(state).filter(
        (item): item is ICartItemWithBundleId => hardwareTypes.includes(item.type) && !!item.bundleId,
    );
};

export const getHardwareEsimTypeForCustomerType: (
    state: State,
    bundleId?: string,
    customerType?: CustomerType,
) => EsimType | undefined = (state, bundleId, customerType) => {
    const supportedEsimTypes = _get(getItemByBundleId(state, bundleId)[0], "supportedEsimTypes");

    if (
        (customerType === CUSTOMER_TYPE.EXISTING || customerType === CUSTOMER_TYPE.EXISTING_OTHER) &&
        supportedEsimTypes?.includes("ESIM-PUSH")
    ) {
        return "ESIM-PUSH";
    } else if (supportedEsimTypes?.includes("ESIM")) {
        return "ESIM";
    } else if (supportedEsimTypes?.includes("ESIM-QR")) {
        return "ESIM-QR";
    }

    return undefined;
};

export const isEsimOnlyCompatible: (state: State, bundleId?: string) => boolean = (state, bundleId) => {
    const rankedSimCardTypes = _get(getItemByBundleId(state, bundleId)[0], "rankedSimCardTypes");
    if (_isEmpty(rankedSimCardTypes)) {
        return false;
    }
    return !rankedSimCardTypes?.some((simCard) => simCard.simCardType === "NANO");
};

// Prices
export const getTotalSumForPaymentType: (state: State, paymentType: CartPaymentTypeValue) => number = (
    state,
    paymentType,
) =>
    getFlattenedCartContent(state).reduce((totalPrice, item) => {
        if (item.type === CART_ITEM_TYPE.MOBILE_REFUND) {
            return totalPrice;
        }

        const quantity = _get(item, "quantity.value", 1);
        const totalItemPrice = quantity * _get(item, paymentType, 0);
        let totalDiscount = quantity * _get(item, "discount.value.upfront", 0);

        if (paymentType === CART_PAYMENT_TYPE.UPFRONT) {
            totalDiscount = quantity * _get(item, "discount.value.upfront", 0);
        } else if (paymentType === CART_PAYMENT_TYPE.MONTHLY) {
            totalDiscount = quantity * _get(item, "discount.value.monthly", 0);
        } else if (paymentType === CART_PAYMENT_TYPE.FIRSTINVOICE) {
            return totalPrice + _get(item, "price.firstInvoice", 0);
        }

        if (totalPrice + totalItemPrice - totalDiscount < 0) {
            return 0;
        }

        return totalPrice + totalItemPrice - totalDiscount;
    }, 0);
export const getTotalPriceFirstInvoice: (state: State) => number = (state) =>
    getTotalSumForPaymentType(state, CART_PAYMENT_TYPE.FIRSTINVOICE);
export const getTotalPriceUpfront: (state: State) => number = (state) =>
    getTotalSumForPaymentType(state, CART_PAYMENT_TYPE.UPFRONT);
export const getTotalPriceMonthly: (state: State) => number = (state) =>
    getTotalSumForPaymentType(state, CART_PAYMENT_TYPE.MONTHLY);
export const getTotalPriceUpfrontWithoutVAT: (cart: State) => number = (cart) =>
    getTotalSumForPaymentType(cart, CART_PAYMENT_TYPE.UPFRONT_WITHOUT_VAT);

// Sums up all items of type "NEW_NUMBER"
export const getNewNumberPrice: (state: State) => number = (state) =>
    getFlattenedCartContent(state).reduce((totalPrice, item) => {
        const quantity = _get(item, "quantity.value", 1);
        const totalItemPrice = quantity * _get(item, CART_PAYMENT_TYPE.FIRSTINVOICE, 0);
        const newNumberPrice = item.type === CART_ITEM_TYPE.NEW_NUMBER ? totalItemPrice : 0;
        return totalPrice + newNumberPrice;
    }, 0);

export const hasScreenOrPlusInsurance: (state: State) => boolean = (state) => {
    return !!getFlattenedCartContent(state).find((cartItem) => {
        const cartItemId = _get(cartItem, "id", "");
        return _startsWith(cartItemId, "INSURANCE_PLUS") || _startsWith(cartItemId, "INSURANCE_SCREEN");
    });
};

export const hasLikeFinInsurance: (state: State) => boolean = (state) => {
    return !!getFlattenedCartContent(state).find((cartItem) => {
        const cartItemId = _get(cartItem, "id", "");
        return _startsWith(cartItemId, "INSURANCE") && _endsWith(cartItemId, "PREOWNED");
    });
};

export const hasViaplayCampaign: (state: State, subscriptionId: string) => boolean = (state, subscriptionId) => {
    const cartItem = getItemByBundleId(state, subscriptionId)[0];
    return !!_find(_get(cartItem, "items"), (item) => {
        const id = _get(item, "id", "");
        return _startsWith(id, "NCVPL");
    });
};

export const getFirstBundleItemInCart: (state: State) => ICartItemWithBundleId | undefined = (state) =>
    getFlattenedCartContent(state).find((cartItem) => !!cartItem.bundleId) as ICartItemWithBundleId | undefined;

export const getFirstPhoneItemInCart: (state: State) => ICartItemWithBundleId | undefined = (state) => {
    return getFlattenedCartContent(state).find(
        (cartItem) => cartItem.type === CART_ITEM_TYPE.HANDSET || cartItem.type === CART_ITEM_TYPE.HANDSET_PREOWNED,
    ) as ICartItemWithBundleId | undefined;
};

// Subscriptions
export const hasDraftMobileSubscriptions: (state: State) => boolean = (state) =>
    _size(
        getAllItemsOfType(state, CART_ITEM_TYPE.SUBSCRIPTION_DRAFT).filter(
            (item) => !isSubscriptionBroadband(state, item.id),
        ),
    ) > 0;

/** @deprecated get rid of isLegal */
export const isLegalSubscriptionDraft: (state: State) => boolean = (state) => {
    const legalSubscriptionId = _get(getLegalSubscription(state), "id");
    // Bundled HW also have bundleId. Only interrested in subscriptions here.
    const flattenedCartContent = getFlattenedCartContent(state);
    const subscriptionsWithBundleId = flattenedCartContent.filter(
        (item) => CART_ITEM_SUBSCRIPTION_TYPES.includes(item.type) && item.bundleId === legalSubscriptionId,
    );
    const legalSubscriptionCartItem: ICartItem | undefined = _get(subscriptionsWithBundleId, "[0]");
    return _get(legalSubscriptionCartItem, "type") === CART_ITEM_TYPE.SUBSCRIPTION_DRAFT;
};

export const isSubscriptionDraft: (state: State, id: string) => boolean = (state, id) => {
    const flattenedCartContent = getFlattenedCartContent(state);
    const subscriptionsWithBundleId = flattenedCartContent.filter(
        (item) => CART_ITEM_SUBSCRIPTION_TYPES.includes(item.type) && item.bundleId === id,
    );
    const subscriptionCartItem: ICartItem | undefined = _get(subscriptionsWithBundleId, "[0]");
    return _get(subscriptionCartItem, "type") === CART_ITEM_TYPE.SUBSCRIPTION_DRAFT;
};

export const getSubscriptionItemFromBundleId: (state: State, id: string) => ICartItem | undefined = (state, id) => {
    const flattenedCartContent = getFlattenedCartContent(state);
    const subscriptionsWithBundleId = flattenedCartContent.filter(
        (item) => CART_ITEM_SUBSCRIPTION_TYPES.includes(item.type) && item.bundleId === id,
    );
    const subscriptionCartItem: ICartItem | undefined = _get(subscriptionsWithBundleId, "[0]");
    return subscriptionCartItem;
};

export const hasSmartDiscountInCart = (state: State): boolean =>
    getFlattenedCartContent(state).some((cartItem) => {
        return cartItem.discount?.isSmartDiscount;
    });

export const getAllServiceSubscriptions = (state: State): ICartItem[] => {
    const subscriptionInCart = getAllItemsOfType(state, ...CART_ITEM_SUBSCRIPTION_TYPES) as ICartItemWithBundleId[];
    return subscriptionInCart.filter((item) => isServiceSubscription(state, item.bundleId));
};

export const getAllOfferingCodes = (state: State): string[] => {
    const subscriptionInCart = getAllItemsOfType(state, ...CART_ITEM_SUBSCRIPTION_TYPES) as ICartItemWithBundleId[];
    const offeringCodes = new Set<string>();
    subscriptionInCart.forEach((item) => {
        offeringCodes.add(item.id?.split(".")[0]);
    });
    return _compact(Array.from(offeringCodes));
};

export const hasOnlyServiceSubscriptions = (state: State): boolean => {
    const numberOfServiceSubscriptions = getAllServiceSubscriptions(state).length;
    return numberOfServiceSubscriptions > 0 && getFlattenedCartContent(state).length === numberOfServiceSubscriptions;
};

export const hasAnyTeliaTryggSubscriptions = (state: State): boolean => {
    const subscriptionInCart = getAllItemsOfType(state, ...CART_ITEM_SUBSCRIPTION_TYPES) as ICartItemWithBundleId[];
    return subscriptionInCart.some((item) => isTeliaTryggSubscription(state, item.bundleId));
};

export const hasAnyTeliaTryggServices = (state: State): boolean => {
    return getAllItemsOfType(state, CART_ITEM_TYPE.SERVICE).some((item) => item.id === TELIA_TRYGG_SERVICE);
};

export const getNumberOfSubscriptionsInCart = (state: State, offeringCode: OfferingCode): number =>
    getAllItemsOfType(state, ...CART_ITEM_SUBSCRIPTION_TYPES).filter((item) => item.id === offeringCode).length;

export const getMobileRefundDisclaimer = (state: State): HighlightedLineProps | undefined => {
    const refundItems = getAllItemsOfType(state, CART_ITEM_TYPE.MOBILE_REFUND);
    if (refundItems.length === 0) {
        return;
    }

    const handsets = getAllItemsOfType(state, CART_ITEM_TYPE.HANDSET, CART_ITEM_TYPE.HANDSET_PREOWNED);
    const handset = handsets[0];
    if (!handset) {
        return;
    }

    const refundItem = refundItems[0];
    const duration = handset.leaseMonths;
    const isLeaseWithDuration = duration && duration !== "now" && duration !== "0";
    const campaignSubtitleItem = refundItem.subtitle?.find(
        (s): s is ICartSubtitleItem => typeof s === "object" && s.description === "Ekstra pant",
    );
    const campaignAmount = campaignSubtitleItem?.priceInfo ? Math.abs(parseInt(campaignSubtitleItem.priceInfo, 10)) : 0;
    const refundUpfront = Math.abs(_get(refundItem, "price.upfront", 0)) + campaignAmount;

    if (isLeaseWithDuration) {
        const totalMonthlyPrice = getTotalPriceMonthly(state);
        const refundMonthly = Math.floor(refundUpfront / Number.parseInt(`${duration}`, 10));
        const newMonthly = Math.max(0, totalMonthlyPrice - refundMonthly);

        return {
            description: "Du får lavere månedspris når mobilen du panter er sendt inn og godkjent",
            heading: "Totalt inkl. mobilpant",
            icon: "phone-recycling",
            isMonthly: true,
            price: `ca ${getFormattedPrice(newMonthly)}/md.`,
        };
    }

    const totalPrice = getTotalPriceUpfront(state);
    const newUpfront = Math.ceil(totalPrice - refundUpfront);

    return {
        description: "Du får tilbakebetalt mobilpanten når mobilen er sendt inn og godkjent",
        heading: "Pris inkl. mobilpant",
        isMonthly: false,
        price: `ca ${getFormattedPrice(newUpfront)}`,
    };
};
